// @ts-nocheck
import { Plus, Repeat, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';
import { c, Section, Field, IconBtn, useStudio, useNode, useShown } from '../ui';

const { FOLLOWUP_MAX_ITEMS: MAX, FOLLOWUP_MESSAGE_MAX: MSG_MAX, FOLLOWUP_TIME_MIN: MIN, FOLLOWUP_TIME_MAX: TMAX } = AI_AGENT_FIELD_LIMITS;

const isBlank = (s) => !String(s.waitTime ?? '').trim() && !(s.message || '').trim();


const rowErrors = (step, i, steps) => {
  const e = {};
  if (isBlank(step)) return e;
  const raw = String(step.waitTime ?? '').trim();
  if (!(step.message || '').trim()) e.message = 'Add the message the agent should say.';
  if (raw === '') {
    e.wait = `Add a wait between ${MIN} and ${TMAX} seconds.`;
    return e;
  }
  const n = Number(raw);
  if (Number.isNaN(n) || n < MIN || n > TMAX) {
    e.wait = `Wait between ${MIN} and ${TMAX} seconds.`;
    return e;
  }
  const prev = steps
    .slice(0, i)
    .map((p) => Number(String(p.waitTime ?? '').trim()))
    .filter((v) => !Number.isNaN(v) && v > 0)
    .pop();
  if (prev !== undefined && n <= prev) e.wait = `Must be longer than the previous follow-up (${prev}s).`;
  return e;
};

export default function FollowUpSection({ next, onNext, value, onChange }) {
  const steps = value?.steps || [];
  const { touch } = useStudio();
  const node = useNode('followup');
  const shown = useShown();

  const emit = (list) => onChange({ ...value, count: list.length, steps: list });
  const update = (i, patch) => emit(steps.map((s, k) => (k === i ? { ...s, ...patch } : s)));

  const add = () => {
    if (steps.length >= MAX) {
      toast.error(`You can add up to ${MAX} follow-ups`);
      return;
    }
    emit([...steps, { waitTime: '', message: '' }]);
  };
  const changeWait = (i, raw) => {
    if (raw !== '' && Number(raw) > TMAX) toast.error(`Wait time can be max ${TMAX} seconds`, { id: 'followup-time-max' });
    update(i, { waitTime: raw });
  };

  const on = steps.some((s) => s.message?.trim() && String(s.waitTime ?? '').trim());
  return (
    <Section
      id="followup"
      icon={Repeat}
      title="Follow-ups"
     sub="Required. Messages the agent sends if the caller goes quiet. Add at least one."
     tag={node.ok ? 'done' : 'required'}
     plainReq
      ok={node.ok}
      next={next('followup')}
      onNext={onNext}
    >
      <p className={c('help')}>
        Up to {MAX}. Wait {MIN} to {TMAX} seconds, and each one must wait longer than the one before.
      </p>
      <div className={c('cnt')} style={{ margin: '0 0 10px' }}>
        <span>
          {steps.length} of {MAX} follow-ups
        </span>
      </div>
      {steps.map((s, i) => {
        const errs = rowErrors(s, i, steps);
        const show = shown(`followup.${i}`);
        return (
          <div
            className={c('form-card')}
            key={i}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) touch(`followup.${i}`);
            }}
          >
            <div className={c('fh')}>
              <Repeat className="ags-i" /> Follow-up {i + 1}
              <span style={{ marginLeft: 'auto' }}>
                <IconBtn icon={Trash2} danger label={`Delete follow-up ${i + 1}`} onClick={() => emit(steps.filter((_, k) => k !== i))} />
              </span>
            </div>
            <div className={c('row2')} style={{ gridTemplateColumns: '150px 1fr' }}>
              <Field label="Wait (seconds)" required={!!s.message?.trim()} style={{ margin: 0 }} note={`${MIN} to ${TMAX}`} error={show && errs.wait}>
                <input
                  className={c('ctl', show && errs.wait && 'invalid')}
                  type="number"
                  min={MIN}
                  max={TMAX}
                  placeholder="e.g. 10"
                  value={s.waitTime}
                  onChange={(e) => changeWait(i, e.target.value)}
                />
              </Field>
              <Field label="Message" required={!!String(s.waitTime ?? '').trim()} style={{ margin: 0 }} count={(s.message || '').length} max={MSG_MAX} note="" error={show && errs.message}>
                <input
                  className={c('ctl', show && errs.message && 'invalid')}
                  value={s.message}
                  maxLength={MSG_MAX}
                  placeholder="Hi, just checking if your issue was resolved?"
                  onChange={(e) => update(i, { message: e.target.value.slice(0, MSG_MAX) })}
                />
              </Field>
            </div>
          </div>
        );
      })}
      <div className={c('addrow')}>
        <button type="button" className={c('btn small')} onClick={add} disabled={steps.length >= MAX}>
          <Plus className="ags-i" />
          Add follow-up
        </button>
      </div>
    </Section>
  );
}
